import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLanguage();

  useEffect(() => { loadPost(); }, [id]);

  const loadPost = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      toast.error(lang === 'en' ? 'Article not found' : 'Articolo non trovato');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="blog-post-loading" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-post-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{lang === 'en' ? 'Article not found' : 'Articolo non trovato'}</h2>
          <Link to="/blog"><Button>{lang === 'en' ? 'Back to Tips' : 'Torna ai Consigli'}</Button></Link>
        </div>
      </div>
    );
  }

  const dateLocale = lang === 'en' ? enUS : it;
  const dateFmt = (d) => { try { return format(new Date(d), 'dd MMMM yyyy', { locale: dateLocale }); } catch { return ''; } };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="blog-post">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-[#0066b2] hover:text-[#005494] mb-8 font-medium" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {lang === 'en' ? 'Back to Tips' : 'Torna ai Consigli'}
          </Link>

          <article>
            {post.image_url && (
              <img src={post.image_url} alt={post.title} className="w-full h-96 object-cover rounded-3xl mb-8 shadow-xl" referrerPolicy="no-referrer" data-testid="blog-post-main-image" />
            )}
            <div className="mb-6">
              <div className="inline-block px-4 py-2 bg-blue-50 text-[#0066b2] text-sm font-medium rounded-full mb-4" data-testid="blog-post-category">{post.category}</div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }} data-testid="blog-post-title">{post.title}</h1>
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span className="flex items-center gap-2" data-testid="blog-post-date">
                  <Calendar className="w-4 h-4" />
                  {dateFmt(post.created_at)}
                </span>
                <span className="flex items-center gap-2" data-testid="blog-post-views">
                  <Eye className="w-4 h-4" />
                  {post.views} {lang === 'en' ? 'views' : 'visualizzazioni'}
                </span>
              </div>
            </div>
            <div className="prose prose-lg max-w-none" data-testid="blog-post-content">
              <p className="text-xl text-gray-700 leading-relaxed mb-8">{post.excerpt}</p>
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">{post.content}</div>
            </div>
            {post.video_url && (
              <div className="mt-8" data-testid="blog-post-video">
                <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>Video</h3>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                  <iframe src={post.video_url} className="w-full h-full" allowFullScreen title="Video" />
                </div>
              </div>
            )}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200" data-testid="blog-post-tags">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full" data-testid={`blog-post-tag-${idx}`}>#{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
}
